// React hook over the adapter's `connectSessionStream` (fn-47.2).
//
// **Transport boundary (fn-47 R2):** the browser SSE primitive lives
// in the CONSUMER's adapter — this hook consumes ONLY the callback
// contract (`onEvent` / `onConnectionChange` / `close`). No transport
// reference ever enters this package (the import-boundary test
// enforces it).
//
// **Lifecycle:**
//   1. On mount (or genuine sessionId transition): reset state, open
//      the stream via `adapter.connectSessionStream`.
//   2. While open: classify ephemeral preview frames FIRST and buffer them
//      for the next animation frame, then normalize + dispatch durable
//      frames into the reducer immediately.
//   3. On unmount / session change: `handle.close()`.
//
// **Reset gating** (.flow memory: seedlive-merge-in-react-hooks):
// `reset` is scoped to a genuine sessionId TRANSITION tracked via a
// `useRef` compare-on-render — NOT to connect-effect dep changes. A
// parent re-render that produces a new seed/adapter reference must
// never wipe already-seeded state.
import { useEffect, useReducer, useRef } from 'react';
import { usePlatformAdapter } from '../context.js';
import { classifyPreviewFrame, initialStreamState, normalizeWireEvent, reduceStream, } from './reduce-stream.js';
/**
 * Subscribe to a session's live event stream (via the adapter from
 * `<PlatformProvider>`) and surface the reduced `StreamState`
 * projection: transcript messages, `RuntimeStatus`, connection state,
 * pending approval, and token totals.
 */
export function useSessionStream(inputs) {
    const adapter = usePlatformAdapter();
    const [state, dispatch] = useReducer(reduceStream, undefined, initialStreamState);
    // Session-id transition reset. Dispatching synchronously during
    // render is safe — `useReducer` allows render-time dispatch so long
    // as the call is gated on a value change (React eats no-op
    // re-renders). New sessions see clean state; same-session re-renders
    // (new seed reference, adapter identity churn) preserve it.
    const sessionIdRef = useRef(null);
    if (sessionIdRef.current !== inputs.sessionId) {
        sessionIdRef.current = inputs.sessionId;
        dispatch({ type: 'reset' });
    }
    // Seed effect: fold history into current state (idempotent via
    // per-event id dedupe). Runs whenever the `seedEvents` reference
    // changes; the reducer rebuilds `[...seed, ...alreadyLive]` so
    // chronology survives a live-before-seed race.
    const seed = inputs.seedEvents ?? null;
    useEffect(() => {
        if (seed === null || seed.length === 0)
            return;
        const events = [];
        for (const envelope of seed) {
            const ev = normalizeWireEvent(envelope);
            if (ev !== null)
                events.push(ev);
        }
        if (events.length === 0)
            return;
        dispatch({ type: 'seed', events });
    }, [seed]);
    const sessionId = inputs.sessionId;
    useEffect(() => {
        if (sessionId === null || sessionId.length === 0)
            return;
        // rAF coalescing for PREVIEW frames only. **Change one, change both** —
        // kept in LOCKSTEP with the console hook
        // (`packages/console/src/streams/use-session-stream.ts`). MA emits token
        // deltas far faster than a paint and each arrives in its own task, so a
        // per-frame dispatch is a per-frame render; flushing from ONE animation
        // frame lets React 18 batch a burst of N deltas into ≤1 commit.
        //
        // DURABLE events are never delayed: the durable branch below cancels the
        // pending frame and flushes synchronously FIRST, so buffered deltas land
        // before the durable event that tombstones their id.
        const previewQueue = [];
        let rafHandle = null;
        const flushPreviews = () => {
            rafHandle = null;
            if (previewQueue.length === 0)
                return;
            const frames = previewQueue.splice(0, previewQueue.length);
            for (const f of frames)
                dispatch({ type: 'preview', frame: f });
        };
        const cancelFlush = () => {
            if (rafHandle === null)
                return;
            const pending = rafHandle;
            rafHandle = null;
            globalThis.cancelAnimationFrame?.(pending);
        };
        const schedulePreview = (frame) => {
            previewQueue.push(frame);
            if (rafHandle !== null)
                return;
            // No rAF (non-browser host): commit immediately rather than never.
            if (typeof globalThis.requestAnimationFrame !== 'function') {
                flushPreviews();
                return;
            }
            rafHandle = globalThis.requestAnimationFrame(flushPreviews);
        };
        /**
         * EVERY connection-state dispatch goes through here: the reducer clears
         * preview buffers on a connection transition, and a frame queued on the
         * dying connection would land AFTER that clear — its `start` re-opening a
         * buffer nothing will ever finalize, which `selectActivePreview` then
         * renders as a ghost bubble of the dead connection's partial text.
         */
        const setConnection = (connectionState) => {
            cancelFlush();
            previewQueue.length = 0;
            dispatch({ type: 'connection', state: connectionState });
        };
        // Optimistic `connecting` so the UI shows progress before the
        // adapter's transport reports its first state.
        setConnection('connecting');
        let handle = null;
        try {
            handle = adapter.connectSessionStream({
                sessionId,
                onEvent: (envelope) => {
                    // Preview frames are classified FIRST, on the body's `type` prefix:
                    // they carry no SSE `id:` line, so `envelope.id` (which adapters fill
                    // from `MessageEvent.lastEventId`) still holds the PRECEDING DURABLE
                    // event's id, and normalizing one would alias the preview onto that
                    // message and feed durable-id dedupe.
                    const preview = classifyPreviewFrame(envelope.data);
                    if (preview !== null) {
                        schedulePreview(preview);
                        return;
                    }
                    // fn-95 (R5) — flush-before-durable: a durable frame is
                    // dispatched in THIS task (never held for a frame), and any
                    // buffered deltas are applied first so preview state can never
                    // be reordered around the durable event that finalizes it.
                    cancelFlush();
                    flushPreviews();
                    const event = normalizeWireEvent(envelope);
                    // Unusable payloads are skipped — the adapter owns error
                    // surfacing for malformed frames.
                    if (event === null)
                        return;
                    dispatch({ type: 'event', event, lastEventId: envelope.id });
                },
                onConnectionChange: (connectionState) => {
                    setConnection(connectionState);
                },
            });
        }
        catch {
            // Connection error at construction time — surface via state.
            setConnection('closed');
            return;
        }
        return () => {
            handle?.close();
            setConnection('closed');
        };
    }, [sessionId, adapter]);
    return state;
}
//# sourceMappingURL=use-session-stream.js.map